import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import xhr from 'xhr';

const PERIOD_OPTIONS = [
  { value: 'primer', label: 'Primer periodo' },
  { value: 'segundo', label: 'Segundo periodo' },
  { value: 'tercer', label: 'Tercer periodo' },
  { value: 'cuarto', label: 'Cuarto periodo' },
];

function getSubjectParts(item) {
  const a = item?.attributes || item;

  const subject = a?.subject?.data
    ? { id: a.subject.data.id, ...(a.subject.data.attributes || {}) }
    : a?.subject || null;

  const grade = a?.grade?.data
    ? { id: a.grade.data.id, ...(a.grade.data.attributes || {}) }
    : a?.grade || null;

  return {
    id: item?.id || a?.id,
    subjectId: subject?.id ? Number(subject.id) : null,
    subjectName: subject?.name || 'N/A',
    period: subject?.period || subject?.attributes?.period || null,
    gradeId: grade?.id ? Number(grade.id) : null,
    cameraPercentage: a?.camera_percentage ?? 100,
  };
}

export default function CameraNotesModal({
  isOpen,
  onClose,
  student,
  gradeId,
  teacherSubjectIds = [],
}) {
  const [period, setPeriod] = useState('primer');
  const [loading, setLoading] = useState(false);
  const [cameraNotes, setCameraNotes] = useState([]);

  const allowedSubjectIds = useMemo(
    () => new Set((teacherSubjectIds || []).map(Number)),
    [teacherSubjectIds]
  );

  useEffect(() => {
    const loadCameraNotes = async () => {
      if (!isOpen || !student?.id || !gradeId) {
        setCameraNotes([]);
        return;
      }

      if (!teacherSubjectIds?.length) {
        setCameraNotes([]);
        return;
      }

      setLoading(true);

      try {
        const url =
          `/subject-enrollments?` +
          `filters[student][id][$eq]=${Number(student.id)}` +
          `&filters[grade][id][$eq]=${Number(gradeId)}` +
          `&pagination[limit]=-1` +
          `&populate=subject` +
          `&populate=grade`;

        const res = await xhr(url);
        const list = res?.data?.data || [];

        const filtered = list
          .map(getSubjectParts)
          .filter((item) => item.subjectId && allowedSubjectIds.has(Number(item.subjectId)))
          .filter((item) => item.period === period);

        setCameraNotes(filtered);
      } catch (error) {
        console.error('Error cargando notas de cámara:', error);
        setCameraNotes([]);
      } finally {
        setLoading(false);
      }
    };

    loadCameraNotes();
  }, [
    isOpen,
    student?.id,
    gradeId,
    period,
    teacherSubjectIds,
    allowedSubjectIds,
  ]);

  const getColorScheme = (value) => {
    const num = Number(value);

    if (num >= 80) return 'green';
    if (num >= 60) return 'yellow';
    return 'red';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          Notas de cámara {student?.name ? `- ${student.name}` : ''}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody fontSize="sm">
          <Flex mb={4} gap={3} align="center">
            <Text fontWeight="bold">Periodo:</Text>

            <Select
              width="220px"
              size="sm"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Flex>

          {loading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : !cameraNotes?.length ? (
            <Text color="gray.600">
              No hay notas de cámara para este estudiante en el periodo seleccionado.
            </Text>
          ) : (
            <Stack spacing={3}>
              {cameraNotes.map((item) => (
                <Box key={item.id} p={3} bg="gray.50" rounded="md">
                  <Flex justify="space-between" align="center" gap={3}>
                    <Box>
                      <Text fontWeight="semibold">{item.subjectName}</Text>
                      <Text fontSize="xs" color="gray.500">
                        Periodo: {item.period || 'N/A'}
                      </Text>
                    </Box>

                    <Badge
                      colorScheme={getColorScheme(item.cameraPercentage)}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {item.cameraPercentage}%
                    </Badge>
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}